import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { mediaData, getScoreColor, channelIcons } from '../data/mockData';
import GlobalFilters from '../components/GlobalFilters';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiTrendingUp, FiDollarSign, FiTarget, FiUsers } = FiIcons;

const ChannelDeepDive = () => {
  const { mediaId, channelId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('performance');

  const media = mediaData.find(m => m.id === parseInt(mediaId));
  const channel = media?.channels[channelId];

  if (!media || !channel) {
    return <div>Data not found</div>;
  }

  const performanceData = {
    xAxis: {
      type: 'category',
      data: ['1 พ.ย.', '5 พ.ย.', '10 พ.ย.', '15 พ.ย.', '20 พ.ย.', '25 พ.ย.', '30 พ.ย.']
    },
    yAxis: [
      {
        type: 'value',
        name: 'ROAS',
        position: 'left'
      },
      {
        type: 'value',
        name: 'CPA (฿)',
        position: 'right'
      }
    ],
    series: [
      {
        name: 'ROAS',
        type: 'line',
        data: [4.2, 4.8, 5.1, 5.5, 5.2, 5.8, 6.0],
        smooth: true,
        lineStyle: { color: '#3B82F6' },
        itemStyle: { color: '#3B82F6' }
      },
      {
        name: 'CPA',
        type: 'line',
        yAxisIndex: 1,
        data: [180, 165, 150, 140, 150, 135, 120],
        smooth: true,
        lineStyle: { color: '#EF4444' },
        itemStyle: { color: '#EF4444' }
      }
    ],
    tooltip: { trigger: 'axis' },
    legend: { data: ['ROAS', 'CPA'] }
  };

  const audienceData = {
    gender: {
      tooltip: { trigger: 'item' },
      series: [{
        type: 'pie',
        radius: '70%',
        data: [
          { value: 60, name: 'หญิง', itemStyle: { color: '#EC4899' } },
          { value: 40, name: 'ชาย', itemStyle: { color: '#3B82F6' } }
        ],
        label: { show: true, formatter: '{b}: {c}%' }
      }]
    },
    age: {
      xAxis: {
        type: 'category',
        data: ['18-24', '25-34', '35-44', '45-54', '55+']
      },
      yAxis: { type: 'value' },
      series: [{
        type: 'bar',
        data: [15, 35, 30, 15, 5],
        itemStyle: { color: '#10B981' }
      }],
      tooltip: { trigger: 'axis' }
    }
  };

  const tabs = [
    { id: 'performance', label: 'ประสิทธิภาพ', icon: FiTrendingUp },
    { id: 'audience', label: 'กลุ่มเป้าหมาย', icon: FiUsers },
    { id: 'creative', label: 'รายละเอียดโฆษณา', icon: FiTarget }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <motion.button
              onClick={() => navigate(`/media/${mediaId}`)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiArrowLeft} className="h-5 w-5 text-gray-600" />
            </motion.button>
            <div className="flex items-center space-x-4">
              <img 
                src={media.thumbnail} 
                alt={media.name} 
                className="w-16 h-16 rounded-lg object-cover"
              />
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{channelIcons[channelId]}</span>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{media.name}</h1>
                  <p className="text-gray-500">{channel.name} • {media.product}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Global Filters - No performance, search, or date filters */}
        <GlobalFilters 
          showPerformanceFilter={false} 
          showChannelFilter={false} 
          showSearchFilter={false}
          showDateFilter={false}
        />

        {/* Key Metrics Overview */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-green-600">{channel.roas}x</div>
                <div className="text-sm text-gray-500">ROAS</div>
              </div>
              <SafeIcon icon={FiTrendingUp} className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-blue-600">฿{channel.spent.toLocaleString()}</div>
                <div className="text-sm text-gray-500">งบประมาณที่ใช้</div>
              </div>
              <SafeIcon icon={FiDollarSign} className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-purple-600">{channel.conversions}</div>
                <div className="text-sm text-gray-500">Conversions</div>
              </div>
              <SafeIcon icon={FiTarget} className="h-8 w-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-orange-600">฿{channel.cpa}</div>
                <div className="text-sm text-gray-500">CPA</div>
              </div>
              <div className={`text-xs font-medium px-2 py-1 rounded-full ${getScoreColor(channel.score)}`}>
                {channel.score}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={tab.icon} className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'performance' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">แนวโน้มประสิทธิภาพ</h3>
                  <div className="h-80">
                    <ReactECharts option={performanceData} style={{ height: '100%' }} />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Impressions</div>
                    <div className="text-xl font-semibold">{channel.impressions.toLocaleString()}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Clicks</div>
                    <div className="text-xl font-semibold">{channel.clicks.toLocaleString()}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">CTR</div>
                    <div className="text-xl font-semibold">{channel.ctr}%</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">CVR</div>
                    <div className="text-xl font-semibold">{channel.cvr}%</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'audience' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">การแบ่งตามเพศ</h3>
                    <div className="h-64">
                      <ReactECharts option={audienceData.gender} style={{ height: '100%' }} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">การแบ่งตามอายุ</h3>
                    <div className="h-64">
                      <ReactECharts option={audienceData.age} style={{ height: '100%' }} />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">5 จังหวัดยอดนิยม</h3>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {['กรุงเทพฯ', 'เชียงใหม่', 'ขอนแก่น', 'สงขลา', 'นครราชสีมา'].map((province, index) => (
                      <div key={province} className="bg-gray-50 p-4 rounded-lg text-center">
                        <div className="text-lg font-semibold text-gray-900">{province}</div>
                        <div className="text-sm text-gray-500">{(25 - index * 3)}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'creative' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">รายละเอียดโฆษณา</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Headline</label>
                        <div className="mt-1 p-3 bg-gray-50 rounded-lg">
                          "ลดแรงส่งท้ายปี 11.11! รองเท้าวิ่งคุณภาพ ลดสูงสุด 70%"
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Ad Copy</label>
                        <div className="mt-1 p-3 bg-gray-50 rounded-lg">
                          "ช้อปเลยวันนี้ สินค้าลดสูงสุด 70% พร้อมส่งฟรีทั่วประเทศ! รองเท้าวิ่งรุ่น Pro-X เทคโนโลยีล่าสุด สำหรับนักวิ่งมืออาชีพ"
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Call to Action</label>
                        <div className="mt-1 p-3 bg-blue-100 text-blue-800 rounded-lg font-medium">
                          "Shop Now"
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">ตัวอย่างโฆษณา</h3>
                    <img 
                      src={media.thumbnail} 
                      alt={media.name} 
                      className="w-full rounded-lg shadow-sm" 
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default ChannelDeepDive;